import { useNavigate } from "react-router-dom";
import * as http from "../../../../utils/http";
import * as translator from "../../../../utils/translator";
import { useCartContext } from "../../../../customHooks/useCart";
import { getReorderNavigationParams } from "../../ReorderTrackingManager";
import { buildReorderShippingFields } from "../../reorderShippingFields";

export interface ReorderCartSelection {
  /** Legacy plan only; null for Care+ / one-time. */
  subscriptionPlanID: number | string | null;
  appliedSubscriptionDiscount: number;
  isSftd: boolean;
  /** Saved to cart.options.experiments for Paid Order. */
  experiments?: Record<string, string>;
}

/** Saves the reorder to the cart and navigates on. Shared by both reorder screens. */
export const useReorderCartSubmit = (reorderData: any, country: string, language: string, setStickyNotificationDetails: any) => {
  const navigate = useNavigate();
  const { getCart } = useCartContext();

  const saveReorderToCart = ({ subscriptionPlanID, appliedSubscriptionDiscount, isSftd, experiments }: ReorderCartSelection) => {
    const productItems = reorderData.orderProductItems;
    const cartProductsDTOs = productItems
      .filter((item: any) => item.isAvailableForReorder)
      .map((item: any) => ({
        quantity: item.quantity,
        product_item_id: item.product_item_id,
        only_prescription: reorderData.isOnlyPrescription ? 1 : 0,
        catalog_id: item.catalog_id,
        product_in_stock: reorderData.isOnlyPrescription ? 0 : 1, // Enables the out-of-stock prescription buying mechanism
      }));

    const shippingFields = buildReorderShippingFields({
      selectedServiceOption: reorderData.selectedServiceOption,
      expressShippingCompany: reorderData.expressShippingCompany,
      expressShippingTier: reorderData.expressShippingTier,
    });

    const chooseTreatmentPostBody = {
      cartProductsDTOs: cartProductsDTOs,
      product_item_id: productItems[0].product_item_id,
      item_price: productItems[0].product_item_price,
      subscription_plan_id: subscriptionPlanID,
      treatment_fee: productItems[0].treatment_fee,
      catalog_id: productItems[0].category_catalog_id,
      applied_subscription_discount: appliedSubscriptionDiscount,
      item_confirmed: 1,
      is_sftd: isSftd ? 1 : 0,
      product_in_stock: reorderData.isOnlyPrescription ? 0 : 1, //To turn on the buy prescription out of stock mechanism in shipping
      only_prescription: reorderData.isOnlyPrescription ? 1 : 0,
      allow_only_prescription: reorderData.isOnlyPrescription ? 1 : 0, //Customer should be able to order only prescription if the original order was prescription only
      selected_service_option: reorderData.selectedServiceOption,
      ...shippingFields,
      owner_id: productItems[0].owner_id,
      owner_type: productItems[0].owner_type,
      ...(experiments ? { experiments } : {}),
    };

    //The data saved and posted to the same endpoint as the other original choose treatment pages.
    return http.postToServer(
      `products/${country}/${language}/${reorderData.catalogMention}/false`,
      chooseTreatmentPostBody,
      [http.MIDDLEWARES.CART_ORDER]
    );
  };

  const goToNextStep = () => {
    const navParams = new URLSearchParams(getReorderNavigationParams()).toString();
    if (getCart().should_id_verify) {
      navigate(`/customer-verification/${country}/${language}?${navParams}`);
    } else {
      navigate(`/shipping/${country}/${language}?${navParams}`);
    }
  };

  const showSubmitError = () => {
    setStickyNotificationDetails((currentDetails: any) => ({
      show: true,
      title: translator.translate(language, "default", `StickyNotification/danger_title`),
      content: "Server Failed",
      level: "error",
      showInfoIcon: false,
      refresh: !currentDetails.refresh,
    }));
  };

  return { saveReorderToCart, goToNextStep, showSubmitError };
};
